import { Row, GridRow, Column } from '../types';

/**
 * Checks if any column defines titleRowTemplates, meaning we need title rows.
 */
function hasTitleRowTemplates<DR>(columns?: Column<DR>[]): boolean {
  return columns?.some((col) => col.titleRowTemplates && col.titleRowTemplates.length > 0) ?? false;
}

export function convertDataTreeToGridRows<DR>(rows: Row<DR>[], columns?: Column<DR>[]): GridRow<DR>[] {
  const result: GridRow<DR>[] = [];
  const needsTitleRows = hasTitleRowTemplates(columns);

  function traverse(row: Row<DR>, depth = 0): void {
    // For top-level rows, insert a title row first (if columns have titleRowTemplates)
    if (depth === 0 && needsTitleRows) {
      const titleRow: GridRow<DR> = {
        ...row,
        rowId: `title-${row.rowId}`,
        type: 'title',
        row,
        depth,
      };
      result.push(titleRow);
    }

    const gridRow: GridRow<DR> = {
      ...row,
      type: 'data',
      row,
      depth,
    };
    result.push(gridRow);

    if (row.isExpanded && row.children) {
      row.children.forEach((child) => traverse(child, depth + 1));
    }
  }

  rows.forEach((row) => traverse(row));
  return result;
}

export function isGridDataRow<DR>(row: GridRow<DR>): row is GridRow<DR> & { type: 'data' } {
  return row.type === 'data';
}

export function isGridTitleRow<DR>(row: GridRow<DR>): row is GridRow<DR> & { type: 'title' } {
  return row.type === 'title';
}

export function getRowHeight<DR>(
  row: GridRow<DR>,
  dataRowHeight: number,
  titleRowHeight: number,
  rowGap: number,
): number {
  const baseHeight = isGridTitleRow(row) ? titleRowHeight : dataRowHeight;
  return baseHeight + rowGap;
}

export function isAtBottom(event: React.UIEvent<HTMLDivElement>, threshold: number): boolean {
  const target = event.target as HTMLDivElement;
  const { scrollTop, scrollHeight, clientHeight } = target;
  return scrollHeight - scrollTop - clientHeight < threshold;
}
